import { NavLink } from "@/components/NavLink";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Home, 
  Flame, 
  Trophy, 
  BarChart3, 
  Settings, 
  Radio,
  Sparkles,
  Rocket,
  Crosshair,
  UserCheck,
  Fish,
  Globe,
  Code,
  HelpCircle,
  FileText,
  BookOpen,
  Shield,
  Store,
  MessageSquare,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Brain
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useWallet } from "@/contexts/WalletContext";
import { useIsAdmin } from "@/hooks/use-admin";

const navItems = [
  { icon: Home, label: "Home", href: "/" },
  { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard", requiresAuth: true },
  { icon: Globe, label: "Discover", href: "/discover", badge: "All Tokens" },
  { icon: Flame, label: "Trending", href: "/trending", badge: "Hot" },
  { icon: Sparkles, label: "New", href: "/new" },
  { icon: Trophy, label: "Leaderboard", href: "/leaderboard" },
  { icon: BarChart3, label: "Portfolio", href: "/portfolio" },
  { icon: Radio, label: "Streams", href: "/streams", badge: "Live" },
  { icon: MessageSquare, label: "Community Hub", href: "/community-hub" },
  { icon: Store, label: "Marketplace", href: "/marketplace" },
];

const toolsItems = [
  { icon: Crosshair, label: "Token Sniper", href: "/sniper", badge: "Pro" },
  { icon: Brain, label: "AI Signals", href: "/ai-signals", badge: "AI" },
  { icon: DollarSign, label: "Copy Subs", href: "/copy-subscriptions", badge: "Earn" },
  { icon: UserCheck, label: "Copy Trading", href: "/copy-trading" },
  { icon: Fish, label: "Whale Tracker", href: "/whales" },
  { icon: Radio, label: "Streamer Dashboard", href: "/streamer" },
  { icon: Code, label: "API Access", href: "/api", badge: "$" },
];

const resourceItems = [
  { icon: HelpCircle, label: "Support", href: "/support" },
  { icon: BookOpen, label: "Docs", href: "/docs" },
  { icon: FileText, label: "Terms", href: "/terms" },
];

export function Sidebar() {
  const { connected } = useWallet();
  const { isAdmin } = useIsAdmin();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const navConfig = localStorage.getItem("moonrun_nav_config");
    if (navConfig) {
      const config = JSON.parse(navConfig);
      setCollapsed(config.desktop?.sidebarCollapsed || false);
    }
  }, []);

  const filteredNavItems = navItems.filter(
    (item) => !item.requiresAuth || connected
  );

  // Only show admin link to admins
  const bottomItems = [
    { icon: Settings, label: "Settings", href: "/settings" },
    ...(isAdmin ? [{ icon: Shield, label: "Admin", href: "/admin", badge: "🔒" }] : []),
  ];

  return (
    <aside 
      className={cn(
        "hidden lg:flex flex-col sticky top-16 h-[calc(100vh-4rem)] border-r border-border bg-sidebar-background transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-4 w-6 h-6 rounded-full border border-border bg-background z-10"
      >
        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
      </Button>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {!collapsed && (
          <p className="px-3 py-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">Main</p>
        )}
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/80 transition-all duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group",
              collapsed && "justify-center px-2"
            )}
            activeClassName="bg-sidebar-primary/10 text-sidebar-primary border-l-2 border-sidebar-primary"
          >
            <item.icon className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
            {!collapsed && (
              <>
                <span className="font-medium text-sm flex-1">{item.label}</span>
                {item.badge && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-primary/20 text-primary">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}

        <div className="pt-4 mt-4 border-t border-border">
          {!collapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">Trading Tools</p>
          )}
          {toolsItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/80 transition-all duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group",
                collapsed && "justify-center px-2"
              )}
              activeClassName="bg-sidebar-primary/10 text-sidebar-primary border-l-2 border-sidebar-primary"
            >
              <item.icon className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
              {!collapsed && (
                <>
                  <span className="font-medium text-sm flex-1">{item.label}</span>
                  {item.badge && (
                    <span className={cn(
                      "px-1.5 py-0.5 text-[10px] font-bold rounded",
                      item.badge === "Pro" && "bg-yellow-500/20 text-yellow-500",
                      item.badge === "$" && "bg-success/20 text-success",
                      item.badge === "Earn" && "bg-success/20 text-success"
                    )}>
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="pt-4 mt-4 border-t border-border">
          {!collapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">Resources</p>
          )}
          {resourceItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/80 transition-all duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group",
                collapsed && "justify-center px-2"
              )}
              activeClassName="bg-sidebar-primary/10 text-sidebar-primary border-l-2 border-sidebar-primary"
            >
              <item.icon className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
              {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="p-3 space-y-1 border-t border-border">
        {bottomItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sidebar-foreground/80 transition-all duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground group",
              collapsed && "justify-center px-2",
              item.label === "Admin" && "text-destructive hover:text-destructive"
            )}
            activeClassName="bg-sidebar-primary/10 text-sidebar-primary"
          >
            <item.icon className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110" />
            {!collapsed && (
              <>
                <span className="font-medium text-sm flex-1">{item.label}</span>
                {item.badge && <span className="text-xs">{item.badge}</span>}
              </>
            )}
          </NavLink>
        ))}

        {!collapsed ? (
          <Link
            to="/create"
            className="flex items-center gap-2 px-4 py-3 mt-2 rounded-xl bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 text-primary hover:border-primary/50 transition-all group"
          >
            <Rocket className="h-5 w-5 transition-transform group-hover:rotate-12" />
            <div>
              <p className="font-semibold text-sm">Create Coin</p>
              <p className="text-[10px] text-primary/70">From 0.02 SOL</p>
            </div>
          </Link>
        ) : (
          <Link
            to="/create"
            className="flex items-center justify-center p-2.5 mt-2 rounded-xl bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 text-primary hover:border-primary/50 transition-all"
          >
            <Rocket className="h-5 w-5" />
          </Link>
        )}
      </div>
    </aside>
  );
}
